import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './HomePage.css';

const HomePage: React.FC = () => {
  const { t } = useTranslation('landing');
  const [apiStatus, setApiStatus] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  const testApiConnection = async () => {
    setIsLoading(true);
    setApiStatus(null);
    
    // Test all endpoints to diagnose the issue
    const results = [];
    
    // Test 1: Direct connection to root health endpoint
    try {
      console.log('Testing direct connection to root health endpoint...');
      const directRootResponse = await axios.get('http://localhost:8000/health', { 
        timeout: 5000,
        headers: { 'Cache-Control': 'no-cache' }
      });
      results.push(`✅ Direct root health: ${JSON.stringify(directRootResponse.data)}`);
    } catch (error: any) {
      console.error('Direct root health error:', error);
      results.push(`❌ Direct root health failed: ${error.message || 'Unknown error'}`);
    }
    
    // Test 2: Direct connection to API health endpoint
    try {
      console.log('Testing direct connection to API health endpoint...');
      const directApiResponse = await axios.get('http://localhost:8000/api/v1/health', { 
        timeout: 5000,
        headers: { 'Cache-Control': 'no-cache' }
      });
      results.push(`✅ Direct API health: ${JSON.stringify(directApiResponse.data)}`);
    } catch (error: any) {
      console.error('Direct API health error:', error);
      results.push(`❌ Direct API health failed: ${error.message || 'Unknown error'}`);
    }
    
    // Test 3: API connection using the API client
    try {
      console.log('Testing API connection using the API client...');
      const apiClientResponse = await axios.get('http://localhost:8000/api/v1/health', { 
        timeout: 5000,
        headers: { 'Cache-Control': 'no-cache' }
      });
      results.push(`✅ API client health: ${JSON.stringify(apiClientResponse.data)}`);
    } catch (error: any) {
      console.error('API client health error:', error);
      results.push(`❌ API client health failed: ${error.message || 'Unknown error'}`);
    }
    
    // Test 4: API connection to users/me endpoint (expecting 401)
    try {
      console.log('Testing API connection to users/me endpoint...');
      await axios.get('http://localhost:8000/api/v1/users/me', { 
        timeout: 5000,
        headers: { 'Cache-Control': 'no-cache' }
      });
      results.push(`❓ API users/me: Unexpected success (should return 401)`);
    } catch (error: any) {
      if (error.response && error.response.status === 401) {
        console.log('Got expected 401 from users/me endpoint');
        results.push(`✅ API users/me: Got expected 401 Unauthorized`);
      } else {
        console.error('API users/me error:', error);
        results.push(`❌ API users/me failed: ${error.message || 'Unknown error'}`);
      }
    }
    
    // Display all results
    setApiStatus(results.join('\n'));
    setIsLoading(false);
  };
  return (
    <div className="home-page">
      <section className="hero">
        <div className="hero-content">
          <h1>{t('home.title')}</h1>
          <p className="hero-subtitle">
            {t('home.subtitle')}
          </p>
          <div className="hero-buttons">
            <Link to="/login" className="btn btn-primary">{t('home.get_started')}</Link>
            <a href="#features" className="btn btn-secondary">{t('home.learn_more')}</a>
          </div>
        </div>
      </section>

      <section id="features" className="features">
        <h2 className="section-title">{t('home.features_title')}</h2>
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">📊</div>
            <h3>{t('home.feature1_title')}</h3>
            <p>{t('home.feature1_text')}</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">🤖</div>
            <h3>{t('home.feature2_title')}</h3>
            <p>{t('home.feature2_text')}</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">📈</div>
            <h3>{t('home.feature3_title')}</h3>
            <p>{t('home.feature3_text')}</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">💾</div>
            <h3>{t('home.feature4_title')}</h3>
            <p>{t('home.feature4_text')}</p>
          </div>
        </div>
      </section>

      <section className="how-it-works">
        <h2 className="section-title">{t('home.how_it_works_title')}</h2>
        <div className="steps">
          <div className="step">
            <div className="step-number">1</div>
            <h3>{t('home.step1_title')}</h3>
            <p>{t('home.step1_text')}</p>
          </div>
          <div className="step">
            <div className="step-number">2</div>
            <h3>{t('home.step2_title')}</h3>
            <p>{t('home.step2_text')}</p>
          </div>
          <div className="step">
            <div className="step-number">3</div>
            <h3>{t('home.step3_title')}</h3>
            <p>{t('home.step3_text')}</p>
          </div>
        </div>
      </section>

      <section className="cta">
        <h2>{t('home.cta_title')}</h2>
        <p>{t('home.cta_text')}</p>
        <Link to="/login" className="btn btn-primary">{t('home.cta_button')}</Link>
      </section>
      
      <section className="api-test">
        <h2 className="section-title">API Connection Test</h2>
        <div className="api-test-container">
          <button 
            onClick={testApiConnection} 
            className="btn btn-secondary"
            disabled={isLoading}
          >
            {isLoading ? 'Checking...' : 'Test API Connection'}
          </button>
          
          {apiStatus && (
            <div className={`api-status ${apiStatus.includes('❌') ? 'error' : 'success'}`}>
              {apiStatus}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default HomePage;
